import type { ReleaseState } from '@processpuzzle/util';
import { Injectable } from '@angular/core';
import { BaseEntityMapper } from '@processpuzzle/base-entity';
import { EventAction, EventDefinition, EventKind, EventParameter, EventParameterType } from './event-definition';

/** One parameter of an event definition as `base-event-api.yaml` puts it on the wire. */
export interface EventParameterDto {
  name?: string;
  type?: EventParameterType | string;
  entityType?: string | null;
  required?: boolean;
  description?: string | null;
}

/** An event definition as `base-event-api.yaml` puts it on the wire. */
export interface EventDefinitionDto {
  id?: string;
  name?: string;
  description?: string;
  kind?: EventKind | string;
  subjectType?: string;
  action?: EventAction | string;
  state?: string;
  parameters?: EventParameterDto[];
  version?: number;
  releaseState?: ReleaseState;
}

/**
 * Translates between the `EventDefinition` DTO and the entity the generated screens work with.
 *
 * Fields are listed one by one rather than spread, so a control the form may gain later cannot leak into
 * the payload unnoticed.
 *
 * `toDto` drops the subject fields of a non-`SYSTEM` event, and `state` unless the action is
 * `STATE_CHANGED`. base-entity has no conditional visibility, so a user who switches the kind keeps whatever
 * the hidden-in-meaning fields held; sending them would make the backend refuse a definition the form shows
 * as valid. Empty strings — what a cleared text box writes back — are sent as absent for the same reason.
 *
 * **The parameters are mapped element by element**, never passed through — an embedded row is edited as the
 * entity the generic form builds, so it reaches `toDto` as whatever the form wrote back. A parameter's
 * `entityType` is dropped unless its type is `ENTITY_REF`, for the reason above. `parameters` is always
 * sent: `PUT /event-definitions/{id}` is a full replacement, so removing the last row has to send `[]`.
 */
@Injectable({ providedIn: 'root' })
export class EventDefinitionMapper implements BaseEntityMapper<EventDefinition> {
  fromDto(dto: unknown): EventDefinition {
    const source = dto as EventDefinitionDto;
    return new EventDefinition({
      id: source.id,
      name: source.name,
      description: source.description,
      kind: source.kind as EventKind | undefined,
      subjectType: source.subjectType,
      action: source.action as EventAction | undefined,
      state: source.state,
      parameters: (source.parameters ?? []).map(toEventParameter),
      version: source.version,
      releaseState: source.releaseState,
    });
  }

  toDto(entity: EventDefinition): EventDefinitionDto {
    const isSystem = entity.kind === EventKind.SYSTEM;
    const action = isSystem ? presentOrUndefined(entity.action) : undefined;
    return {
      id: entity.id,
      name: entity.name,
      description: presentOrUndefined(entity.description),
      kind: entity.kind,
      subjectType: isSystem ? presentOrUndefined(entity.subjectType) : undefined,
      action,
      state: action === EventAction.STATE_CHANGED ? presentOrUndefined(entity.state) : undefined,
      parameters: (entity.parameters ?? []).map(fromEventParameter),
      version: entity.version,
    };
  }
}

function toEventParameter(dto: EventParameterDto): EventParameter {
  return new EventParameter({
    name: dto.name,
    type: dto.type as EventParameterType | undefined,
    entityType: dto.entityType ?? undefined,
    required: dto.required,
    description: dto.description ?? undefined,
  });
}

function fromEventParameter(parameter: EventParameter): EventParameterDto {
  return {
    name: parameter.name,
    type: parameter.type,
    entityType: parameter.type === EventParameterType.ENTITY_REF ? presentOrUndefined(parameter.entityType) : undefined,
    // A checkbox never touched leaves whatever the blank row held; the contract defaults it to false.
    required: parameter.required === true,
    description: presentOrUndefined(parameter.description),
  };
}

function presentOrUndefined<T extends string>(value: T | undefined | null): T | undefined {
  return value === null || value === undefined || value.trim() === '' ? undefined : value;
}
